import { CoreHelper, LoggerHelper, WithProps, i18n, TranslationResources } from 'ff-shared-components-core';
import React, { ReactNode, ComponentType } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { processFlowClear, processFlowInit, processFlowMarkActionDone } from '../../store/processFlow/actions';
import { ProcessFlowState, ProcessFlowStepStoreState } from '../../store/processFlow/types';
import { getProcessFlow } from '../../store/processFlow/selectors';
import './styles.less';

interface ProcessFlowProps {
	baseUrl: string;
	initialStepName?: string;
	resources: TranslationResources;
	onChangeStep?: () => void;
	subHeaderWithFlowStepIndicator?: any; //React.ComponentType<ProgressIndicatorProps>;
	flowStepIndicatorContainerClassName?: string;
}

interface ProcessFlowInjectedProps {
	history: any;
	location: any;
	match: any;
	processFlow: ProcessFlowState;
	init: (flowId: string, flowUrl: string, steps: ProcessFlowStepStoreState[], initialStepName?: string) => void;
	clear: (flowId: string) => void;
	markActionDone: () => void;
}

class ProcessFlow extends React.Component<ProcessFlowProps & ProcessFlowInjectedProps> {
	private flowId: string;

	public constructor(props: ProcessFlowProps & ProcessFlowInjectedProps) {
		super(props);

		const { init, initialStepName, baseUrl, resources } = props;

		Object.keys(resources).forEach(key => {
			i18n.addResourceBundle(key, 'translations', resources[key].translations);
			// LoggerHelper.log(`Process Flow >> Constructor >> key: ${key} >> resource: `, resources[key].translations);
		});

		this.flowId = CoreHelper.guid();

		const steps: ProcessFlowStepStoreState[] = [];

		this.steps.forEach((elem, index) => {
			steps.push({
				index,
				name: elem.props.name,
				ignoreHistory: elem.props.ignoreHistory === undefined ? false : elem.props.ignoreHistory,
			});
		});

		init(this.flowId, baseUrl, steps, initialStepName);
	}

	private get steps(): any[] {
		const { children } = this.props;

		return React.Children.toArray(children);
	}

	private get currentStep(): React.ReactElement | null {
		// LoggerHelper.log('ProcessFlow > currentStep', this.steps);

		const { processFlow } = this.props;
		const currentStepIndex = processFlow.currentStepIndex;

		// LoggerHelper.log('ProcessFlow > currentStep', currentStepIndex);

		if (currentStepIndex >= this.steps.length) {
			return null;
		}

		return this.steps[currentStepIndex];
	}

	public shouldComponentUpdate(nextProps: ProcessFlowProps & ProcessFlowInjectedProps): any {
		const { history } = this.props;
		const { processFlow } = nextProps;

		// LoggerHelper.log('ProcessFlow > shouldComponentUpdate', processFlow.id, this.flowId);

		if (processFlow.id !== this.flowId) {
			return false;
		}

		if (processFlow.restore) {
			// LoggerHelper.log('ProcessFlow > shouldComponentUpdate > restore', processFlow.restore.flowUrl);

			history.push(processFlow.restore.flowUrl);
			return false;
		}

		return true;
	}

	public componentDidUpdate(prevProps: ProcessFlowProps & ProcessFlowInjectedProps): any {
		const { processFlow, onChangeStep, markActionDone } = this.props;

		if (
			prevProps !== undefined &&
			prevProps.processFlow !== undefined &&
			processFlow !== undefined &&
			processFlow.currentStepIndex !== prevProps.processFlow.currentStepIndex
		) {
			markActionDone();
			if (onChangeStep) {
				onChangeStep();
			}
		}
	}

	public render(): any {
		// LoggerHelper.log('ProcessFlow > render', this.props, this.currentStep);

		const { processFlow, subHeaderWithFlowStepIndicator, flowStepIndicatorContainerClassName } = this.props;

		if (!this.currentStep || processFlow.restore) {
			return null;
		}

		let stepChildWrapper: React.ReactNode;
		const stepChildComponent = (
			<div>{React.cloneElement(this.currentStep, { processFlow: { ...this.props } })}</div>
		);

		if (subHeaderWithFlowStepIndicator) {
			stepChildWrapper = (
				<div>
					{React.cloneElement(subHeaderWithFlowStepIndicator, {
						step: processFlow.currentStepIndex + 1,
					})}

					{stepChildComponent}
				</div>
			);
		} else {
			stepChildWrapper = stepChildComponent;
		}

		return (
			<CSSTransition in={!processFlow.action} classNames="transitionContainer fade" timeout={100} unmountOnExit>
				{stepChildWrapper}
			</CSSTransition>
		);
	}

	public componentWillUnmount(): any {
		// LoggerHelper.log('ProcessFlow > componentWillUnmount', this.props);
		const { clear } = this.props;

		clear(this.flowId);
	}
}

const mapStateToProps = (state: any) => ({
	processFlow: getProcessFlow(state),
});

const mapDispatchToProps = (dispatch: any) => ({
	init: (flowId: string, flowUrl: string, steps: ProcessFlowStepStoreState[], initialStepName?: string) => {
		dispatch(processFlowInit(flowId, flowUrl, steps, initialStepName));
	},
	clear: (flowId: string) => {
		dispatch(processFlowClear(flowId));
	},
	markActionDone: () => {
		dispatch(processFlowMarkActionDone());
	},
});

export default WithProps<ProcessFlowProps, ProcessFlowInjectedProps>(
	connect(mapStateToProps, mapDispatchToProps)(withRouter(ProcessFlow))
);
